import React, { useState, useMemo } from 'react';
import { Coin } from '../../data/coins';
import { SetSeriesItem, calculateSetCircReward, calculateSetRareReward } from '../lib/albumData';
import { AlbumSlot } from './AlbumSlot';

interface SetsAndSeriesViewProps {
  userAlbum: Record<string, number>;
  sets?: SetSeriesItem[];
  onHoverCoin: (coin: Coin, count: number, e: React.MouseEvent<HTMLElement>) => void;
  onLeaveCoin: () => void;
  onClickCoin: (coin: Coin, count: number, e: React.MouseEvent<HTMLElement>) => void;
  onClaimReward?: (setId: string, rewardType: 'circulation' | 'rare') => Promise<{ success: boolean; rewardAmount?: number; error?: string }>;
}

type SetCategory = 'all' | 'joint' | 'series' | 'daterun';
type DenominationFilter = 'all' | '2euro' | '1euro' | '50cent' | '20cent' | '10cent' | '5cent' | '2cent' | '1cent';

const DENOMINATION_FILTER_OPTIONS: Array<{ key: DenominationFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: '2euro', label: '2€' },
  { key: '1euro', label: '1€' },
  { key: '50cent', label: '50c' },
  { key: '20cent', label: '20c' },
  { key: '10cent', label: '10c' },
  { key: '5cent', label: '5c' },
  { key: '2cent', label: '2c' },
  { key: '1cent', label: '1c' }
];

function getCircRarityClass(coins: Coin[]): string {
  if (coins.length === 0) return 'badge-standard';
  const minMintage = Math.min(...coins.map(c => c.mintage));
  if (minMintage <= 500_000) return 'badge-u500k';
  if (minMintage <= 1_000_000) return 'badge-u1m';
  if (minMintage <= 5_000_000) return 'badge-u5m';
  return 'badge-standard';
}

function getRarestClass(coins: Coin[]): string {
  if (coins.some(c => c.mintage === 0)) return 'badge-nifc';
  if (coins.some(c => c.mintage <= 100_000)) return 'badge-u100k';
  return getCircRarityClass(coins);
}

function getSetDenomination(set: SetSeriesItem): string {
  if (set.coins && set.coins.length > 0 && set.coins[0].denomination) {
    return set.coins[0].denomination;
  }
  const id = set.id.toLowerCase();
  if (id.endsWith('_1euro')) return '1euro';
  if (id.endsWith('_50cent')) return '50cent';
  if (id.endsWith('_20cent')) return '20cent';
  if (id.endsWith('_10cent')) return '10cent';
  if (id.endsWith('_5cent')) return '5cent';
  if (id.endsWith('_2cent')) return '2cent';
  if (id.endsWith('_1cent')) return '1cent';
  return '2euro';
}

export const SetsAndSeriesView: React.FC<SetsAndSeriesViewProps> = ({
  userAlbum,
  sets: propSets,
  onHoverCoin,
  onLeaveCoin,
  onClickCoin,
  onClaimReward
}) => {
  const [denomFilter, setDenomFilter] = useState<DenominationFilter>('all');
  const [category, setCategory] = useState<SetCategory>('all');
  const [expandedSets, setExpandedSets] = useState<Record<string, boolean>>({
    joint_treaty_of_rome_2007: true,
    series_german_bundeslaender_1: true
  });
  const [claimingKey, setClaimingKey] = useState<string | null>(null);

  const allSets = useMemo(() => {
    return propSets || [];
  }, [propSets]);

  // Handle denomination selection
  const handleDenomChange = (newDenom: DenominationFilter) => {
    setDenomFilter(newDenom);
    // If selecting a non-2E denomination (which only has dateruns), reset category to all
    if (newDenom !== 'all' && newDenom !== '2euro') {
      setCategory('all');
    }
  };

  // Compute set stats & sort by completion % descending, then alphabetically
  const processedSets = useMemo(() => {
    // 1. Filter by denomination first
    let filteredByDenom = allSets;
    if (denomFilter !== 'all') {
      filteredByDenom = allSets.filter(s => getSetDenomination(s) === denomFilter);
    }

    // 2. Filter by category (only applicable when denomFilter is 'all' or '2euro')
    const rawFiltered =
      denomFilter !== 'all' && denomFilter !== '2euro'
        ? filteredByDenom
        : category === 'all'
        ? filteredByDenom
        : filteredByDenom.filter(s => s.category === category);

    const withStats = rawFiltered.map(set => {
      const circCoins = set.coins.filter(c => c.mintage > 100_000);
      const rareCoins = set.coins.filter(c => c.mintage <= 100_000);

      const circTotal = circCoins.length;
      let circOwned = 0;
      for (const c of circCoins) {
        if ((userAlbum[c.id] || 0) > 0) circOwned++;
      }
      const circPctNum = circTotal > 0 ? (circOwned / circTotal) * 100 : 0;
      const circPctStr = circPctNum.toFixed(1);

      const rareTotal = rareCoins.length;
      let rareOwned = 0;
      for (const c of rareCoins) {
        if ((userAlbum[c.id] || 0) > 0) rareOwned++;
      }
      const rarePctNum = rareTotal > 0 ? (rareOwned / rareTotal) * 100 : 0;
      const rarePctStr = rarePctNum.toFixed(1);

      const isCircComplete = circTotal > 0 && circOwned === circTotal;
      const isRareComplete = rareTotal > 0 && rareOwned === rareTotal;
      const isFullyComplete =
        set.coins.length > 0 &&
        (circTotal === 0 || isCircComplete) &&
        (rareTotal === 0 || isRareComplete);

      const circReward = set.stats?.circ_reward_amount !== undefined && Number(set.stats.circ_reward_amount) > 0
        ? Number(set.stats.circ_reward_amount)
        : calculateSetCircReward(set.coins);
      const isCircRewardClaimed = Boolean(set.stats?.is_circ_reward_claimed);

      const rareReward = set.stats?.rare_reward_amount !== undefined && Number(set.stats.rare_reward_amount) > 0
        ? Number(set.stats.rare_reward_amount)
        : calculateSetRareReward(set.coins);
      const isRareRewardClaimed = Boolean(set.stats?.is_rare_reward_claimed);

      const sortPct = circTotal > 0 ? circPctNum : rarePctNum;

      return {
        set,
        circTotal,
        circOwned,
        circPctNum,
        circPctStr,
        rareTotal,
        rareOwned,
        rarePctNum,
        rarePctStr,
        isCircComplete,
        isRareComplete,
        isFullyComplete,
        circReward,
        isCircRewardClaimed,
        rareReward,
        isRareRewardClaimed,
        sortPct
      };
    });

    return withStats.sort((a, b) => {
      if (b.sortPct !== a.sortPct) {
        return b.sortPct - a.sortPct;
      }
      return a.set.title.localeCompare(b.set.title);
    });
  }, [allSets, denomFilter, category, userAlbum]);

  const toggleExpand = (id: string) => {
    setExpandedSets(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleClaim = async (setId: string, rewardType: 'circulation' | 'rare', e: React.MouseEvent) => {
    e.stopPropagation();
    const key = `${setId}_${rewardType}`;
    if (!onClaimReward || claimingKey) return;
    setClaimingKey(key);
    try {
      await onClaimReward(setId, rewardType);
    } finally {
      setClaimingKey(null);
    }
  };

  const showCategoryPills = denomFilter === 'all' || denomFilter === '2euro';

  return (
    <div className="sets-series-view">
      {/* Filters Toolbar */}
      <div className="sets-toolbar">
        {/* Denomination Filter Pills */}
        <div className="sets-category-pills sets-denom-pills">
          {DENOMINATION_FILTER_OPTIONS.map(opt => (
            <button
              key={opt.key}
              className={`category-pill-btn denom-pill-btn ${denomFilter === opt.key ? 'active' : ''}`}
              onClick={() => handleDenomChange(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Category Filter Pills (Only visible when All or 2E is selected) */}
        {showCategoryPills && (
          <div className="sets-category-pills sets-type-pills">
            <button
              className={`category-pill-btn ${category === 'all' ? 'active' : ''}`}
              onClick={() => setCategory('all')}
            >
              All Types
            </button>
            <button
              className={`category-pill-btn ${category === 'joint' ? 'active' : ''}`}
              onClick={() => setCategory('joint')}
            >
              Joint Commemoratives
            </button>
            <button
              className={`category-pill-btn ${category === 'series' ? 'active' : ''}`}
              onClick={() => setCategory('series')}
            >
              Country Series
            </button>
            <button
              className={`category-pill-btn ${category === 'daterun' ? 'active' : ''}`}
              onClick={() => setCategory('daterun')}
            >
              Standard Dateruns
            </button>
          </div>
        )}
      </div>

      {/* Set Cards List */}
      <div className="sets-container-list">
        {processedSets.map(
          ({
            set,
            circTotal,
            circOwned,
            circPctStr,
            rareTotal,
            rareOwned,
            rarePctStr,
            isCircComplete,
            isRareComplete,
            isFullyComplete,
            circReward,
            isCircRewardClaimed,
            rareReward,
            isRareRewardClaimed
          }) => {
            const isExpanded = Boolean(expandedSets[set.id]);

            return (
              <div
                key={set.id}
                className={`set-card ${isFullyComplete || isCircComplete ? 'complete' : ''}`}
              >
                <div className="set-card-header" onClick={() => toggleExpand(set.id)}>
                  <div className="set-header-left">
                    <div className="set-title-row">
                      <span className="set-expand-caret">{isExpanded ? '▼' : '▶'}</span>
                      <span className="set-title">{set.title}</span>
                    </div>
                    {set.subtitle && <span className="set-subtitle">{set.subtitle}</span>}
                  </div>

                  <div className="set-header-right">
                    {circTotal > 0 && isCircComplete && !isCircRewardClaimed && (
                      <button
                        className="claim-set-reward-btn header-claim-btn"
                        onClick={(e) => handleClaim(set.id, 'circulation', e)}
                        disabled={claimingKey === `${set.id}_circulation`}
                      >
                        {claimingKey === `${set.id}_circulation` ? 'Claiming...' : `Claim +€${circReward.toFixed(2)}`}
                      </button>
                    )}

                    {rareTotal > 0 && isRareComplete && !isRareRewardClaimed && (
                      <button
                        className="claim-set-reward-btn header-claim-btn"
                        onClick={(e) => handleClaim(set.id, 'rare', e)}
                        disabled={claimingKey === `${set.id}_rare`}
                      >
                        {claimingKey === `${set.id}_rare` ? 'Claiming...' : `Claim +€${rareReward.toFixed(2)} (Rare)`}
                      </button>
                    )}

                    {circTotal > 0 && (
                      <div className="set-progress-block">
                        <span className="set-progress-text">
                          <span className="set-progress-label">Circ:</span>{' '}
                          <strong>{circOwned}</strong> / {circTotal} ({circPctStr}%)
                        </span>
                        <div className="set-progress-track">
                          <div
                            className={`set-progress-fill ${isCircComplete ? 'complete' : ''}`}
                            style={{ width: `${circPctStr}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {rareTotal > 0 && (
                      <div className="set-progress-block">
                        <span className="set-progress-text">
                          <span className="set-progress-label">U100k & NIFC:</span>{' '}
                          <strong>{rareOwned}</strong> / {rareTotal} ({rarePctStr}%)
                        </span>
                        <div className="set-progress-track">
                          <div
                            className={`set-progress-fill ${isRareComplete ? 'complete' : ''}`}
                            style={{ width: `${rarePctStr}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="set-drawer-body">
                    {/* Rewards Section inside Drawer Body */}
                    {(circReward > 0 || rareReward > 0) && (
                      <div className="set-drawer-rewards-bar">
                        {circTotal > 0 && circReward > 0 && (
                          <div className="set-drawer-reward-item">
                            <span className="reward-item-label">Circulation Reward:</span>
                            {isCircComplete && !isCircRewardClaimed ? (
                              <button
                                className="claim-set-reward-btn"
                                onClick={(e) => handleClaim(set.id, 'circulation', e)}
                                disabled={claimingKey === `${set.id}_circulation`}
                              >
                                {claimingKey === `${set.id}_circulation` ? 'Claiming...' : `Claim +€${circReward.toFixed(2)}`}
                              </button>
                            ) : isCircRewardClaimed ? (
                              <span className="set-reward-claimed-badge">
                                Claimed +€{circReward.toFixed(2)}
                              </span>
                            ) : (
                              <span className="set-reward-potential-badge">
                                €{circReward.toFixed(2)} (Uncompleted)
                              </span>
                            )}
                          </div>
                        )}

                        {rareTotal > 0 && rareReward > 0 && (
                          <div className="set-drawer-reward-item">
                            <span className="reward-item-label">Rare & NIFC Reward:</span>
                            {isRareComplete && !isRareRewardClaimed ? (
                              <button
                                className="claim-set-reward-btn"
                                onClick={(e) => handleClaim(set.id, 'rare', e)}
                                disabled={claimingKey === `${set.id}_rare`}
                              >
                                {claimingKey === `${set.id}_rare` ? 'Claiming...' : `Claim +€${rareReward.toFixed(2)}`}
                              </button>
                            ) : isRareRewardClaimed ? (
                              <span className="set-reward-claimed-badge">
                                Claimed +€${rareReward.toFixed(2)}
                              </span>
                            ) : (
                              <span className="set-reward-potential-badge">
                                €{rareReward.toFixed(2)} (Uncompleted)
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="set-strip-table-wrapper">
                      <table className="set-single-row-table">
                        <thead>
                          <tr>
                            {set.columns.map((col, idx) => (
                              <th key={idx} className="set-col-th">
                                <div>{col.label}</div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {set.columns.map((col, idx) => (
                              <td key={idx} className="set-col-td">
                                <div className="set-col-coins-flex">
                                  {col.coins.map(coin => (
                                    <AlbumSlot
                                      key={coin.id}
                                      coin={coin}
                                      count={userAlbum[coin.id] || 0}
                                      size={col.coins.length > 1 ? 'small' : 'normal'}
                                      onHover={onHoverCoin}
                                      onLeave={onLeaveCoin}
                                      onClick={onClickCoin}
                                    />
                                  ))}
                                </div>
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          }
        )}
      </div>
    </div>
  );
};
